/*eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  TextField,
  Button,
  DialogContent,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Grid,
  LinearProgress,
  Fade,
  Slide,
  alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const GoalsDialog = ({
  open,
  handleClose,
  goals,
  setUpdatedGoalsPayload,
  isSubmittingGoals,
  searchedValue,
  searchFromRecord,
  isTeacher,
  setSearchedValue,
}) => {
  const [currentGoals, setCurrentGoals] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Completed, 2: In Progress

  useEffect(() => {
    if (goals && Array.isArray(goals)) {
      setCurrentGoals(JSON.parse(JSON.stringify(goals)));
    }
  }, [goals]);

  useEffect(() => {
    if (!goals || !Array.isArray(goals)) {
      setHasChanges(false);
      return;
    }
    const changed = JSON.stringify(currentGoals) !== JSON.stringify(goals);
    setHasChanges(changed);
  }, [currentGoals, goals]);

  // Process goals data - handle both API response structures
  const processedGoals = currentGoals.map((goalItem) => {
    const userInfo = isTeacher ? goalItem.goalsFrom : goalItem.goalsFor;
    const userName = userInfo?.fname && userInfo?.lname
      ? `${userInfo.fname} ${userInfo.lname}`
      : userInfo?.fname || userInfo?.email || 'Unknown User';
    const userImage = userInfo?.image || '';

    return {
      ...goalItem,
      userName,
      userImage,
      goalTitle: goalItem.goal?.title || goalItem.title || 'Untitled Goal',
      isCompleted: goalItem.goal?.isCompleted || false,
      _id: goalItem._id,
    };
  });

  // Filter goals based on search
  const searchFilteredGoals = searchedValue
    ? processedGoals.filter((goal) =>
        goal.goalTitle.toLowerCase().includes(searchedValue.toLowerCase()) ||
        goal.userName.toLowerCase().includes(searchedValue.toLowerCase())
      )
    : processedGoals;

  // Filter by tab status
  const getFilteredGoalsByTab = () => {
    if (activeTab === 1) {
      return searchFilteredGoals.filter((g) => g.isCompleted);
    }
    if (activeTab === 2) {
      return searchFilteredGoals.filter((g) => !g.isCompleted);
    }
    return searchFilteredGoals;
  };

  const filteredGoals = getFilteredGoalsByTab();

  // Calculate stats
  const completedGoals = processedGoals.filter((g) => g.isCompleted);
  const inProgressGoals = processedGoals.filter((g) => !g.isCompleted);
  const totalGoals = processedGoals.length;

  const handleGoalToggle = (goalId) => {
    const updated = currentGoals.map((goal) => {
      if (goal._id === goalId) {
        return {
          ...goal,
          goal: {
            ...goal.goal,
            isCompleted: !goal.goal?.isCompleted,
          },
        };
      }
      return goal;
    });
    setCurrentGoals(updated);
  };

  const refactorGoalsPayloadForBackend = () => {
    const changedGoals = [];

    if (!goals || !Array.isArray(goals)) {
      setUpdatedGoalsPayload({ goals: changedGoals });
      return;
    }

    currentGoals.forEach((goalItem) => {
      const originalGoal = goals.find((g) => g._id === goalItem._id);
      if (originalGoal && goalItem.goal?.isCompleted !== originalGoal.goal?.isCompleted) {
        const refactoredGoal = { ...goalItem.goal };
        const _id = goalItem._id;
        delete refactoredGoal['_id'];
        changedGoals.push({ _id: _id, goal: refactoredGoal });
      }
    });

    setUpdatedGoalsPayload({ goals: changedGoals });
  };

  const resetGoals = () => {
    if (goals) {
      setCurrentGoals(JSON.parse(JSON.stringify(goals)));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Calculate completion percentage
  const completionPercentage = totalGoals > 0 ? Math.round((completedGoals.length / totalGoals) * 100) : 0;

  return (
    <Dialog
      open={open || false}
      fullScreen
      onClose={handleClose}
      scroll="body"
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'background.default',
          height: '100dvh',
          maxHeight: '100dvh',
          overflowY: 'auto',
          overflowX: 'hidden',
        },
      }}
    >
      {/* Modern Gradient Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha('#A8469B', 0.9)} 0%, ${alpha('#569cd3', 0.8)} 100%)`,
          p: { xs: 3, sm: 4 },
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        {/* Decorative Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: alpha('#fff', 0.1),
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: alpha('#fff', 0.08),
          }}
        />

        {/* Header Content */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#fff', 0.2),
                  backdropFilter: 'blur(10px)',
                }}
              >
                <EmojiEventsIcon sx={{ fontSize: 32, color: '#fff' }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Goals Management
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.95 }}>
                  {isTeacher ? '🎯 Track your professional development goals' : '📊 Monitor and guide mentee progress'}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: '#fff',
                bgcolor: alpha('#fff', 0.15),
                '&:hover': { bgcolor: alpha('#fff', 0.25) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Progress Bar and Stats */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2.5,
              bgcolor: alpha('#fff', 0.15),
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Overall Progress
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {completionPercentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: alpha('#fff', 0.2),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    bgcolor: '#fff',
                    boxShadow: '0 0 10px rgba(255,255,255,0.5)',
                  },
                }}
              />
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 100 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {completedGoals.length}/{totalGoals}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Completed
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          bgcolor: 'background.default',
        }}
      >
        {/* Stats Cards with Glass Effect */}
        <Box sx={{ p: { xs: 2, sm: 4 }, bgcolor: 'background.default' }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha('#569cd3', 0.15)} 0%, ${alpha('#A8469B', 0.1)} 100%)`,
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${alpha('#569cd3', 0.3)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  boxShadow: `0 4px 20px ${alpha('#569cd3', 0.15)}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${alpha('#569cd3', 0.25)}`,
                    border: `2px solid ${alpha('#569cd3', 0.5)}`,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${alpha('#569cd3', 0.25)} 0%, ${alpha('#A8469B', 0.2)} 100%)`,
                      backdropFilter: 'blur(5px)',
                      border: `1.5px solid ${alpha('#569cd3', 0.3)}`,
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 26, color: '#569cd3' }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#569cd3', mb: 0.5 }}>
                      {totalGoals}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Total Goals
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha('#54D62C', 0.15)} 0%, ${alpha('#229A16', 0.1)} 100%)`,
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${alpha('#54D62C', 0.3)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  boxShadow: `0 4px 20px ${alpha('#54D62C', 0.15)}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${alpha('#54D62C', 0.25)}`,
                    border: `2px solid ${alpha('#54D62C', 0.5)}`,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${alpha('#54D62C', 0.25)} 0%, ${alpha('#229A16', 0.2)} 100%)`,
                      backdropFilter: 'blur(5px)',
                      border: `1.5px solid ${alpha('#54D62C', 0.3)}`,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 26, color: '#54D62C' }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#54D62C', mb: 0.5 }}>
                      {completedGoals.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Completed
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha('#FFC107', 0.15)} 0%, ${alpha('#FF9800', 0.1)} 100%)`,
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${alpha('#FFC107', 0.3)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  boxShadow: `0 4px 20px ${alpha('#FFC107', 0.15)}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${alpha('#FFC107', 0.25)}`,
                    border: `2px solid ${alpha('#FFC107', 0.5)}`,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${alpha('#FFC107', 0.25)} 0%, ${alpha('#FF9800', 0.2)} 100%)`,
                      backdropFilter: 'blur(5px)',
                      border: `1.5px solid ${alpha('#FFC107', 0.3)}`,
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 26, color: '#FFC107' }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#FFC107', mb: 0.5 }}>
                      {inProgressGoals.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      In Progress
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Search */}
          {!isTeacher && (
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                placeholder="🔍 Search by goal title or user name..."
                value={searchedValue || ''}
                onChange={(e) => setSearchedValue(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: alpha('#569cd3', 0.2),
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: alpha('#569cd3', 0.4),
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            px: { xs: 2, sm: 4 },
            py: 1,
            bgcolor: 'background.paper',
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: 'linear-gradient(90deg, #A8469B 0%, #569cd3 100%)',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                minHeight: 56,
                px: 3,
                color: 'text.secondary',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: alpha('#569cd3', 0.05),
                },
                '&.Mui-selected': {
                  color: '#A8469B',
                  fontWeight: 700,
                },
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>All Goals</span>
                  <Chip
                    label={totalGoals}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      bgcolor: activeTab === 0 ? alpha('#A8469B', 0.15) : alpha('#919EAB', 0.1),
                      color: activeTab === 0 ? '#A8469B' : 'text.secondary',
                    }}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Completed</span>
                  <Chip
                    label={completedGoals.length}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      bgcolor: activeTab === 1 ? alpha('#54D62C', 0.15) : alpha('#919EAB', 0.1),
                      color: activeTab === 1 ? 'success.main' : 'text.secondary',
                    }}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>In Progress</span>
                  <Chip
                    label={inProgressGoals.length}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      bgcolor: activeTab === 2 ? alpha('#FFC107', 0.15) : alpha('#919EAB', 0.1),
                      color: activeTab === 2 ? 'warning.main' : 'text.secondary',
                    }}
                  />
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Goals List - Horizontal Scrollable */}
        <Box
          sx={{
            flex: 1,
            minHeight: 'auto',
            overflowX: { xs: 'hidden', sm: 'auto' },
            overflowY: 'visible',
            p: { xs: 2, sm: 3 },
            '&::-webkit-scrollbar': {
              height: '10px',
            },
            '&::-webkit-scrollbar-track': {
              background: (theme) => alpha(theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#f1f1f1', 0.5),
              borderRadius: '5px',
              margin: '0 16px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: (theme) => alpha(theme.palette.primary.main, 0.4),
              borderRadius: '5px',
              '&:hover': {
                background: (theme) => alpha(theme.palette.primary.main, 0.6),
              },
            },
          }}
        >
          {filteredGoals.length > 0 ? (
            <Box
              sx={{
                display: { xs: 'grid', sm: 'flex' },
                gridTemplateColumns: { xs: '1fr', sm: 'none' },
                gap: { xs: 2, sm: 3 },
                pb: 2,
                minWidth: { xs: '100%', sm: 'fit-content' },
              }}
            >
              {filteredGoals.map((goalItem, index) => {
                const completed = goalItem.isCompleted;
                return (
                  <Fade in key={goalItem._id} timeout={300 + index * 50}>
                    <Card
                      sx={{
                        minWidth: { xs: '100%', sm: 320, md: 360 },
                        maxWidth: { xs: '100%', sm: 320, md: 360 },
                        height: 'fit-content',
                        border: '2px solid',
                        borderColor: completed ? alpha('#54D62C', 0.3) : alpha('#569cd3', 0.15),
                        borderRadius: 3,
                        bgcolor: completed ? alpha('#54D62C', 0.02) : 'background.paper',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        flexShrink: 0,
                        '&:hover': {
                          transform: 'translateY(-8px) scale(1.02)',
                          boxShadow: completed
                            ? `0 12px 32px ${alpha('#54D62C', 0.25)}`
                            : `0 12px 32px ${alpha('#569cd3', 0.25)}`,
                          borderColor: completed ? 'success.main' : 'primary.main',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Header: Avatar and Checkbox */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Avatar
                              src={goalItem.userImage}
                              sx={{
                                width: 56,
                                height: 56,
                                bgcolor: alpha('#A8469B', 0.15),
                                color: '#A8469B',
                                fontWeight: 700,
                                border: `3px solid ${alpha('#A8469B', 0.2)}`,
                                fontSize: '1.5rem',
                              }}
                            >
                              {!goalItem.userImage && (goalItem.userName?.charAt(0)?.toUpperCase() || 'U')}
                            </Avatar>

                            {/* Checkbox or Success Icon */}
                            <Box
                              onClick={() => !completed && handleGoalToggle(goalItem._id)}
                              sx={{
                                cursor: completed ? 'default' : 'pointer',
                                transition: 'transform 0.2s ease',
                                '&:hover': {
                                  transform: completed ? 'none' : 'scale(1.15)',
                                },
                              }}
                            >
                              {completed ? (
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha('#54D62C', 0.15),
                                  }}
                                >
                                  <CheckCircleIcon sx={{ fontSize: 24, color: 'success.main' }} />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '3px solid',
                                    borderColor: alpha('#569cd3', 0.4),
                                    '&:hover': {
                                      borderColor: 'primary.main',
                                      bgcolor: alpha('#569cd3', 0.05),
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      bgcolor: alpha('#569cd3', 0.3),
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {/* User Info Chip */}
                          <Box sx={{ mb: 2 }}>
                            <Chip
                              label={`${isTeacher ? 'With' : 'Mentee'}: ${goalItem.userName}`}
                              size="small"
                              sx={{
                                height: 26,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                bgcolor: alpha('#A8469B', 0.1),
                                color: '#A8469B',
                                '& .MuiChip-label': { px: 2 },
                                mb: 1,
                              }}
                            />
                            {goalItem.timestamp && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block' }}>
                                📅 {new Date(goalItem.timestamp).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </Typography>
                            )}
                          </Box>

                          {/* Goal Title */}
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: completed ? alpha('#212B36', 0.6) : 'text.primary',
                                textDecoration: completed ? 'line-through' : 'none',
                                lineHeight: 1.5,
                                mb: 2,
                                minHeight: '3em',
                              }}
                            >
                              {goalItem.goalTitle}
                            </Typography>

                            {/* Status Badge */}
                            {completed && (
                              <Chip
                                icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />}
                                label="Achieved"
                                sx={{
                                  height: 32,
                                  fontWeight: 700,
                                  fontSize: '0.8rem',
                                  bgcolor: alpha('#54D62C', 0.15),
                                  color: 'success.main',
                                  border: `2px solid ${alpha('#54D62C', 0.3)}`,
                                  alignSelf: 'flex-start',
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                width: '100%',
              }}
            >
              <Fade in>
                <Card
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    px: 4,
                    maxWidth: 500,
                    bgcolor: 'background.neutral',
                    border: (theme) => `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderRadius: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha('#569cd3', 0.1),
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700, mb: 1 }}>
                    {searchedValue || activeTab > 0 ? 'No Goals Found' : 'No Goals Yet'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {searchedValue
                      ? '🔍 Try adjusting your search terms or filters to find what you\'re looking for'
                      : activeTab > 0
                      ? '📊 Try selecting a different tab to view other goals'
                      : isTeacher
                      ? '🎯 Your edifier will set goals to guide your professional development journey'
                      : '📝 Start setting meaningful goals to guide your mentees\' growth'}
                  </Typography>
                </Card>
              </Fade>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        {hasChanges && (
          <Slide in direction="up">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                p: { xs: 2.5, sm: 3 },
                borderTop: '2px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0px -4px 20px rgba(0, 0, 0, 0.08)',
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                💡 You have unsaved changes
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  onClick={resetGoals}
                  disabled={isSubmittingGoals}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    px: 3,
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      borderWidth: 2,
                      bgcolor: alpha('#919EAB', 0.08),
                    },
                  }}
                >
                  Cancel Changes
                </Button>
                <LoadingButton
                  loading={isSubmittingGoals}
                  variant="contained"
                  onClick={refactorGoalsPayloadForBackend}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 4,
                    width: { xs: '100%', sm: 'auto' },
                    background: 'linear-gradient(135deg, #A8469B 0%, #569cd3 100%)',
                    boxShadow: `0 4px 12px ${alpha('#A8469B', 0.3)}`,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #8e3983 0%, #4788ba 100%)',
                      boxShadow: `0 6px 16px ${alpha('#A8469B', 0.4)}`,
                    },
                  }}
                >
                  Save Changes
                </LoadingButton>
              </Stack>
            </Box>
          </Slide>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GoalsDialog;
